import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { GridApi, ICellEditorParams } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';

import { InputComponent } from '@shared/components/input/input.component';
import { FormControlConstants } from '@shared/constants/form-control.constants';
import { OverlayService } from '@shared/services/overlay.service';

interface IAgDatePickerParams extends ICellEditorParams {
  ignoreValidations?: boolean;
  max?: string;
  min?: string;
  type?: 'date' | 'month';
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, ReactiveFormsModule],
  styles: [
    `
      :host,
      :host ::ng-deep input,
      :host ::ng-deep label,
      .aux-ag-date-picker {
        height: 100%;
      }

      :host ::ng-deep input {
        font-size: 14px;
      }
    `,
  ],
  templateUrl: './ag-date-picker.component.html',
})
export class AgDatePickerComponent implements ICellEditorAngularComp {
  overlayService = inject(OverlayService);

  api!: GridApi;
  fc = new FormControl('');
  readonly formControlConstants = FormControlConstants;
  max = '';
  min = '';
  params!: IAgDatePickerParams;
  stopEditing!: (suppressNavigateAfterEdit?: boolean | undefined) => void;
  type: 'date' | 'month' = 'date';

  agInit(params: IAgDatePickerParams): void {
    this.params = params;

    if (params.api) {
      this.api = params.api;
    }

    if (params.type) {
      this.type = params.type;
    }

    this.min = params.min || '';
    this.max = params.max || '';
    this.stopEditing = params.stopEditing;
    let value = params.value;

    if (this.type === 'month') {
      value = value ? dayjs(value).format('YYYY-MM') : value;
    }

    this.fc.setValue(value);
  }

  getValue() {
    // when you select something from the default browser pop-up it doesn't trigger the blur event in the input,
    // so we need to manually check the validation
    if (this.min) {
      if (dayjs(this.fc.value).isBefore(this.min)) {
        this.overlayService.error(this.formControlConstants.VALIDATION_MESSAGE.MONTH_MIN);

        return null;
      }
    }

    if (this.max) {
      if (dayjs(this.fc.value).isAfter(this.max)) {
        this.overlayService.error(this.formControlConstants.VALIDATION_MESSAGE.MONTH_MAX);

        return null;
      }
    }

    return this.fc.value;
  }
}
